import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, TrendingDown, DollarSign, AlertTriangle, 
  Lightbulb, Shield, RefreshCw, ArrowUpRight, ArrowDownRight,
  Activity, Server, Database, Cloud, Cpu, HardDrive, HelpCircle, Sparkles, GripVertical
} from 'lucide-react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { toast } from 'sonner';
import OnboardingTour from '../components/OnboardingTour';
import RoleBasedWidgets from '../components/RoleBasedWidgets';
import WidgetSettings, { getWidgetPreferences } from '../components/WidgetSettings';
import AIChatHistory from '../components/AIChatHistory';
import DraggableWidgetGrid from '../components/DraggableWidgetGrid';
import CostAlerts from '../components/CostAlerts';
import QuickStart from '../components/QuickStart';
import CloudCostDashboard from '../components/CloudCostDashboard';
import DailyAIBriefWidget from '../components/DailyAIBriefWidget';
import RealTimeUnitEconWidget from '../components/RealTimeUnitEconWidget';
import PilotDiscoveriesCard from '../components/PilotDiscoveriesCard';
import UpgradeCtaBanner from '../components/UpgradeCtaBanner';
import BurningCashTicker from '../components/BurningCashTicker';
import UnifiedSavingsBanner from '../components/UnifiedSavingsBanner';
import OnboardingChecklist from '../components/OnboardingChecklist';
import ValuePropHero from '../components/ValuePropHero';
import GuaranteeWidget from '../components/GuaranteeWidget';
import BillDetectiveCard from '../components/BillDetectiveCard';  // Engine #29 dashboard chiclet
// v34.32 (Feb 2026) — Swapped legacy SDK-adoption WallOfTenantsWidget
// for the new live engine-action ticker (WallOfTenants.js). Backed by
// /api/wall/feed and polls every 8s. Old widget kept on disk for reference.
import WallOfTenants from '../components/WallOfTenants';
// CloudProviderSelector removed — redundant with OnboardingTour's cloud picker step
// import CloudProviderSelector, { useCloudProviderSelection, getTodayProviders } from '../components/CloudProviderSelector';
import { useAWSCredentials } from '../components/AWSConnect';
import { useAzureCredentials } from '../components/AzureConnect';
import { useGCPCredentials } from '../components/GCPConnect';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import { DashboardViewProvider, DashboardViewToggle, DashboardViewSection, useDashboardView } from '../components/DashboardViewToggle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = {
  primary: '#059669',
  secondary: '#0EA5E9',
  tertiary: '#F59E0B',
  quaternary: '#E11D48',
  grid: '#E2E8F0'
};

const SERVICE_COLORS = ['#059669', '#0EA5E9', '#F59E0B', '#E11D48', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16'];

const Dashboard = () => {
  const { shouldShowData } = useDemoData();

  useEffect(() => {
    document.title = 'Dashboard · CARTIEAI';
  }, []);

  const { user, isAdmin, isFinance, isEngineering } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [serviceBreakdown, setServiceBreakdown] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [widgetPrefs, setWidgetPrefs] = useState(getWidgetPreferences());
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [widgetLayout, setWidgetLayout] = useState([]);
  const [dragEnabled, setDragEnabled] = useState(false);
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [todayProviders, setTodayProviders] = useState({ providers: [] });

  // Cloud provider connection status
  const { isConnected: awsConnected } = useAWSCredentials();
  const { isConnected: azureConnected } = useAzureCredentials();
  const { isConnected: gcpConnected } = useGCPCredentials();
  const hasCloudConnection = awsConnected || azureConnected || gcpConnected;

  useEffect(() => {
    fetchDashboardData();
    // Load today's provider selection from localStorage (set by OnboardingTour)
    try {
      const saved = JSON.parse(localStorage.getItem('selected_providers') || '[]');
      setTodayProviders({ providers: saved });
    } catch {
      setTodayProviders({ providers: [] });
    }

    // Check if user has seen the tour. Demo users see the GuidedDemoTour
    // mounted in Layout.js instead — we suppress this one to avoid a
    // double-modal collision (v34.40).
    const userEmail = JSON.parse(localStorage.getItem('user') || '{}').email;
    const isDemoUser = userEmail === 'demo@cartieai.com';
    const hasSeenTour = localStorage.getItem('cartie_tour_completed');
    if (!hasSeenTour && !isDemoUser) {
      setShowTour(true);
    }
    // Check if quick start has been completed or skipped
    const quickStartComplete = localStorage.getItem('cartie_quickstart_complete');
    const quickStartSkipped = localStorage.getItem('cartie_quickstart_skipped');
    if (!quickStartComplete && !quickStartSkipped && !hasSeenTour) {
      // Show quick start after tour or if tour was already seen
      setTimeout(() => {
        const tourDone = localStorage.getItem('cartie_tour_completed');
        if (tourDone && !quickStartComplete && !quickStartSkipped) {
          setShowQuickStart(true);
        }
      }, 1000);
    }
  }, [hasCloudConnection]);

  const handleTourComplete = () => {
    localStorage.setItem('cartie_tour_completed', 'true');
    setShowTour(false);
  };

  const handleQuickStartComplete = (data) => {
    setShowQuickStart(false);
    toast.success(`Setup complete! Monitoring ${data.cloudProvider?.toUpperCase() || 'cloud'} costs.`);
  };

  const handleQuickStartSkip = () => {
    setShowQuickStart(false);
  };

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, trendsRes, forecastRes, anomaliesRes, recsRes, serviceRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/summary`),
        axios.get(`${API_URL}/dashboard/trends?days=30`),
        axios.get(`${API_URL}/forecast?days=30`),
        axios.get(`${API_URL}/anomalies?status=active`),
        axios.get(`${API_URL}/recommendations`),
        axios.get(`${API_URL}/dashboard/by-service`)
      ]);
      
      setSummary(summaryRes.data);
      setTrends(trendsRes.data.trends);
      setForecast(forecastRes.data);
      setAnomalies(anomaliesRes.data);
      setRecommendations(recsRes.data);
      setServiceBreakdown(serviceRes.data.breakdown);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Set empty defaults on error
      setSummary({
        total_cost_mtd: 0,
        total_cost_last_month: 0,
        cost_change_percent: 0,
        anomaly_count: 0,
        potential_savings: 0,
        active_guardrails: 0,
        services_count: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  };

  const fetchAiInsights = async () => {
    setLoadingInsights(true);
    try {
      const response = await axios.post(`${API_URL}/ml/insights/ai`);
      if (response.data.success) {
        setAiInsights(response.data.insights);
      } else {
        setAiInsights(response.data.fallback_insights);
      }
    } catch (error) {
      console.error('Failed to fetch AI insights:', error);
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleWidgetSettingsChange = (newPrefs) => {
    setWidgetPrefs(newPrefs);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-lg rounded-lg p-3 text-sm">
          <p className="font-medium text-slate-900 dark:text-white mb-1">{formatDate(label)}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Combine trends and forecast for the chart
  const combinedChartData = [
    ...trends.map(t => ({ ...t, type: 'actual' })),
    ...forecast.map(f => ({ date: f.date, cost: f.predicted_cost, lower: f.lower_bound, upper: f.upper_bound, type: 'forecast' }))
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800';
      case 'high': return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
      case 'medium': return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-sky-900/30 dark:text-sky-400 dark:border-sky-800';
      default: return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800';
      case 'medium': return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
      default: return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800';
    }
  };

  const getServiceIcon = (service) => {
    if (service.includes('EC2') || service.includes('VM') || service.includes('Compute')) return <Cpu className="w-4 h-4" />;
    if (service.includes('S3') || service.includes('Blob') || service.includes('Storage')) return <HardDrive className="w-4 h-4" />;
    if (service.includes('RDS') || service.includes('Database') || service.includes('BigQuery')) return <Database className="w-4 h-4" />;
    if (service.includes('Lambda') || service.includes('Function')) return <Activity className="w-4 h-4" />;
    return <Cloud className="w-4 h-4" />;
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Dashboard" description="Connect your cloud account to see real data here." />;
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="p-6">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-32" />
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-6">
            <Skeleton className="h-80 w-full" />
          </Card>
          <Card className="p-6">
            <Skeleton className="h-80 w-full" />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <DashboardViewProvider defaultMode={isEngineering && !isFinance ? 'engineer' : 'cfo'}>
    <div className="relative p-6 space-y-6 overflow-hidden" data-testid="dashboard" data-tour="dashboard-hero">
      {/* Subtle brand-color atmosphere orbs (behind content, no interaction) */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-20 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-200/25 blur-[120px]" />
        <div className="absolute top-10 right-1/4 w-[380px] h-[380px] rounded-full bg-cyan-200/25 blur-[120px]" />
        <div className="absolute top-40 left-1/2 -translate-x-1/2 w-[260px] h-[260px] rounded-full bg-blue-200/20 blur-[100px]" />
      </div>

      {/* Upgrade CTA — shows only when activation score ≥70 */}
      <UpgradeCtaBanner />

      {/* v35.18 — Friendly Defaults: value-prop hero + 3-step onboarding for first-time users */}
      <ValuePropHero name={user?.name || user?.full_name || ''} />
      <OnboardingChecklist />

      {/* Unified Savings Banner — cloud + AI savings tied together (Feb 2026) */}
      <UnifiedSavingsBanner />

      {/* Engine #29 — "Who spent money this week?" chiclet (Feb 2026) */}
      <BillDetectiveCard />

      {/* 3× ROI Guarantee live tracking widget (v35.07) */}
      <GuaranteeWidget />

      {/* Quick Start Wizard */}
      {showQuickStart && (
        <QuickStart 
          onComplete={handleQuickStartComplete}
          onSkip={handleQuickStartSkip}
        />
      )}

      {/* Onboarding Tour */}
      <OnboardingTour 
        isOpen={showTour} 
        onClose={() => setShowTour(false)} 
        onComplete={handleTourComplete}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 relative">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold font-['Manrope'] tracking-tight leading-tight">
            <span className="text-slate-900">Cost </span>
            <span className="text-emerald-600">Intelligence</span>
            <span className="text-slate-900"> Dashboard</span>
          </h1>
          <p className="text-slate-600 text-sm mt-1.5 font-medium">
            Welcome back, <span className="text-slate-900 font-semibold">{user?.name}</span>. Here's your cloud cost overview.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DashboardViewToggle className="mr-1" />
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setShowTour(true)}
            className="text-slate-400 hover:text-slate-400"
            title="Take a tour"
          >
            <HelpCircle className="w-5 h-5" />
          </Button>
          <WidgetSettings onSettingsChange={handleWidgetSettingsChange} />
          <Button
            variant={dragEnabled ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setDragEnabled(!dragEnabled);
              toast.success(dragEnabled ? 'Edit mode disabled' : 'Drag widgets to reorder');
            }}
            className={`gap-2 ${dragEnabled ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
            data-testid="drag-toggle-btn"
          >
            <GripVertical className="w-4 h-4" />
            <span className="hidden sm:inline">{dragEnabled ? 'Done' : 'Edit Layout'}</span>
          </Button>
          <AIChatHistory />
          <CostAlerts />
          <Button 
            variant="outline"
            size="sm"
            onClick={fetchAiInsights}
            disabled={loadingInsights}
            className="gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-cyan-800 dark:text-cyan-400"
            data-testid="ai-insights-btn"
          >
            <Sparkles className={`w-4 h-4 ${loadingInsights ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">AI Insights</span>
          </Button>
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* CFO-leading widgets — money first */}
      <DashboardViewSection mode="cfo">
        {/* Daily AI Brief — replaces 30 min of manual morning analysis */}
        <DailyAIBriefWidget />

        {/* Burning Cash live ticker — physical urgency for the "cost of inaction" */}
        <BurningCashTicker />
      </DashboardViewSection>

      {/* Engineer-leading widgets — code-level metrics first */}
      <DashboardViewSection mode="engineer">
        {/* Real-Time Unit Econ — trace-stream-driven, refreshes every 15s */}
        <RealTimeUnitEconWidget />

        {/* Pilot Discoveries — weekly auto-scan surface (Pipeline Dedup et al.) */}
        <PilotDiscoveriesCard />
      </DashboardViewSection>

      {/* Wall of Tenants — public adoption signal (both views) */}
      <WallOfTenants />

      {/* CFO trailing widget — engineer unit-econ shows below the fold in CFO view */}
      <DashboardViewSection mode="cfo">
        <RealTimeUnitEconWidget />
        <PilotDiscoveriesCard />
      </DashboardViewSection>

      {/* Engineer trailing widget — money widgets show below the fold in Engineer view */}
      <DashboardViewSection mode="engineer">
        <DailyAIBriefWidget />
        <BurningCashTicker />
      </DashboardViewSection>

      {/* Cloud Cost Dashboard - Shows real data or empty state */}
      <CloudCostDashboard />

      {/* AI Insights Panel */}
      {aiInsights && (
        <Card className="border-emerald-200 dark:border-cyan-800 bg-emerald-50 bg-gradient-to-r from-emerald-50 to-white dark:from-emerald-950/20 dark:to-slate-900" data-testid="ai-insights-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-emerald-600" />
                <CardTitle className="text-base">AI Cost Insights</CardTitle>
                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-cyan-900/50 dark:text-cyan-400 text-[11px]">
                  Claude 4.5
                </Badge>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setAiInsights(null)}
                className="text-slate-400 h-6 w-6 p-0"
              >
                ×
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <div className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                {aiInsights}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-hover" data-testid="metric-total-cost">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-400 dark:text-slate-400">Total Cost MTD</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                  {formatCurrency(summary?.total_cost_mtd || 0)}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                <DollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-sm">
              {summary?.cost_change_percent >= 0 ? (
                <>
                  <ArrowUpRight className="w-4 h-4 text-rose-500" />
                  <span className="text-rose-500 font-medium">+{summary?.cost_change_percent}%</span>
                </>
              ) : (
                <>
                  <ArrowDownRight className="w-4 h-4 text-emerald-500" />
                  <span className="text-emerald-500 font-medium">{summary?.cost_change_percent}%</span>
                </>
              )}
              <span className="text-slate-400">vs last month</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="metric-anomalies">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-400 dark:text-slate-400">Active Anomalies</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                  {summary?.anomaly_count || 0}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <p className="text-sm text-slate-400 mt-3">Requires attention</p>
          </CardContent>
        </Card>

        <Card className="surface-hero relative overflow-hidden surface-hero-accent-strip card-hover" data-testid="metric-savings">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">Potential Savings</p>
                <p className="text-3xl font-extrabold text-slate-900 mt-1 tabular-nums">
                  {formatCurrency(summary?.potential_savings || 0)}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-100">
                <Lightbulb className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <p className="text-sm text-slate-500 mt-3">Based on {recommendations.length} recommendations</p>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="metric-guardrails">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-400 dark:text-slate-400">Active Guardrails</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                  {summary?.active_guardrails || 0}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-sky-900/30">
                <Shield className="w-5 h-5 text-emerald-600 dark:text-sky-400" />
              </div>
            </div>
            <p className="text-sm text-slate-400 mt-3">Monitoring {summary?.services_count || 0} services</p>
          </CardContent>
        </Card>
      </div>

      {/* Role-Based Quick Actions */}
      {widgetPrefs.roleWidget?.enabled && (
        <RoleBasedWidgets 
          summary={summary} 
          anomalies={anomalies} 
          recommendations={recommendations} 
        />
      )}

      {/* Main Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cost Trend & Forecast Chart */}
        <Card className="lg:col-span-2" data-testid="cost-trend-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold font-['Manrope']">Cost Trend & Forecast</CardTitle>
            <CardDescription>Historical costs with 30-day prediction</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
                <AreaChart data={combinedChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.secondary} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={CHART_COLORS.secondary} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    tick={{ fontSize: 12 }}
                    stroke="#94a3b8"
                  />
                  <YAxis 
                    tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`}
                    tick={{ fontSize: 12 }}
                    stroke="#94a3b8"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="cost" 
                    stroke={CHART_COLORS.primary} 
                    fillOpacity={1} 
                    fill="url(#colorCost)"
                    name="Actual/Predicted Cost"
                    strokeWidth={2}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="upper" 
                    stroke={CHART_COLORS.secondary} 
                    fillOpacity={0.1} 
                    fill={CHART_COLORS.secondary}
                    name="Upper Bound"
                    strokeDasharray="5 5"
                    strokeWidth={1}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="lower" 
                    stroke={CHART_COLORS.secondary} 
                    fillOpacity={0.1} 
                    fill={CHART_COLORS.secondary}
                    name="Lower Bound"
                    strokeDasharray="5 5"
                    strokeWidth={1}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Service Breakdown Pie Chart */}
        <Card data-testid="service-breakdown-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold font-['Manrope']">Cost by Service</CardTitle>
            <CardDescription>Current month breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={200}>
                <PieChart>
                  <Pie
                    data={serviceBreakdown.slice(0, 6)}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="cost"
                    nameKey="service"
                  >
                    {serviceBreakdown.slice(0, 6).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={SERVICE_COLORS[index % SERVICE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 mt-4">
              {serviceBreakdown.slice(0, 5).map((service, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: SERVICE_COLORS[idx % SERVICE_COLORS.length] }} 
                    />
                    <span className="text-slate-400 dark:text-slate-400 truncate max-w-[120px]">
                      {service.service}
                    </span>
                  </div>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {formatCurrency(service.cost)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Section: Anomalies & Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Anomalies Panel */}
        <Card data-testid="anomalies-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold font-['Manrope']">Active Anomalies</CardTitle>
                <CardDescription>Cost spikes requiring attention</CardDescription>
              </div>
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400">
                {anomalies.length} active
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {anomalies.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No active anomalies detected</p>
                </div>
              ) : (
                anomalies.map((anomaly, idx) => (
                  <div 
                    key={idx}
                    className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                    data-testid={`anomaly-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          {getServiceIcon(anomaly.service)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white text-sm">
                            {anomaly.service}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            Expected: {formatCurrency(anomaly.expected_cost)} | Actual: {formatCurrency(anomaly.detected_cost)}
                          </p>
                        </div>
                      </div>
                      <Badge className={`text-xs ${getSeverityColor(anomaly.severity)}`}>
                        {anomaly.severity}
                      </Badge>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-xs text-slate-400">
                        {new Date(anomaly.detected_at).toLocaleString()}
                      </span>
                      <span className={`text-sm font-medium ${anomaly.deviation_percent > 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                        {anomaly.deviation_percent > 0 ? '+' : ''}{anomaly.deviation_percent.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recommendations Panel */}
        <Card data-testid="recommendations-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold font-['Manrope']">Optimization Recommendations</CardTitle>
                <CardDescription>Actions to reduce cloud spend</CardDescription>
              </div>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400">
                {formatCurrency(summary?.potential_savings || 0)} savings
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {recommendations.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Lightbulb className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No recommendations available</p>
                </div>
              ) : (
                recommendations.map((rec, idx) => (
                  <div 
                    key={idx}
                    className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-emerald-500/30 transition-colors"
                    data-testid={`recommendation-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`text-xs ${getPriorityColor(rec.priority)}`}>
                            {rec.priority}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {rec.type.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p className="font-medium text-slate-900 dark:text-white text-sm">
                          {rec.service}
                        </p>
                        <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                          {rec.description}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(rec.potential_savings)}
                        </p>
                        <p className="text-xs text-slate-400">
                          {rec.savings_percent}% savings
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </DashboardViewProvider>
  );
};

export default Dashboard;
